import { InjectProperty } from '@jack-henry/frontend-utils/di';
import '@jack-henry/jh-ui/components/button/button.js';
import { AppType, ConfigurationService } from '@treasury/core/config';
import { NavigationService } from '@treasury/core/navigation';
import { CompanyUserService } from '@treasury/domain/company/user';
import { CompanyUserInformation as CompanyUserInformationEntity } from '@treasury/domain/company/user/entities';
import { TmFormChanged } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/forms/tm-form';
import '@treasury/presentation/components/jh-ui/tm-input';
import { TmButtonType } from '@treasury/presentation/components/tm-button.types';
import { TmWizardStep } from '@treasury/presentation/components/tm-wizard';
import { TmSideDrawerService } from '@treasury/presentation/services/side-drawer';
import { TmToastPosture, TmToastService } from '@treasury/presentation/services/toast';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

interface FormModel {
    name: string;
    email: string;
    loginId: string;
    phone: string;
    fax: string;
    department: string;
    isSuperUser: boolean;
    isAdmin: boolean;
}
@customElement('tm-company-user-information')
export class CompanyUserInformation extends TmWizardStep {
    public stepLabel = 'User Information';

    @property({ type: Number })
    public companyId?: number;

    @property({ type: Number })
    public userId?: number;

    @property({ type: Object })
    public userInfo = new CompanyUserInformationEntity();

    @property({ type: Boolean })
    public isReadOnly = false;

    @state()
    private selectedUser: string = '';

    @state()
    private isValid: boolean = false;

    @InjectProperty()
    declare private readonly config: ConfigurationService;

    @InjectProperty()
    declare private readonly drawerService: TmSideDrawerService;

    @InjectProperty()
    declare private readonly navigationService: NavigationService;

    @InjectProperty()
    declare private readonly companyUserService: CompanyUserService;

    @InjectProperty()
    declare private readonly toastService: TmToastService;

    @state()
    private isSaving = false;

    private get isBoApp() {
        return this.config.app === AppType.BackOffice;
    }

    public validate() {
        return true;
    }

    public render() {
        return html` <div class="flex flex-col gap-4">${this.renderForm()}</div> `;
    }

    private renderAboutSection() {
        return html`<div class="flex flex-col gap-8 w-full lg:w-[50%]">
            <tm-input
                class="w-full"
                ?disabled=${this.isReadOnly}
                .maxLength=${50}
                .minLength=${1}
                .name=${'name'}
                .initial=${this.userInfo.name}
                required
                showIndicator
                .label=${'Name'}
            >
            </tm-input>
            <tm-input
                .type=${'email'}
                class="w-full"
                ?disabled=${this.isReadOnly}
                .maxLength=${50}
                .minLength=${1}
                .name=${'email'}
                .initial=${this.userInfo.email}
                required
                showIndicator
                .label=${'Email'}
            >
            </tm-input>
            <tm-input
                class="w-full"
                ?disabled=${this.isReadOnly}
                .maxLength=${50}
                .minLength=${1}
                .name=${'loginId'}
                .initial=${this.userInfo.loginId}
                required
                showIndicator
                .label=${'Login ID'}
            >
            </tm-input>
            <tm-input
                .type=${'tel'}
                class="w-full"
                ?disabled=${this.isReadOnly}
                .maxLength=${50}
                .minLength=${1}
                .name=${'phone'}
                .initial=${this.userInfo.phone}
                required
                showIndicator
                .label=${'Phone'}
            >
            </tm-input>
            <div class="flex flex-row justify-between gap-5">
                <tm-input
                    class="w-full"
                    ?disabled=${this.isReadOnly}
                    .maxLength=${50}
                    .name=${'department'}
                    .initial=${this.userInfo.department}
                    .label=${'Department'}
                >
                </tm-input>
                <tm-input
                    .type=${'tel'}
                    class="w-full"
                    ?disabled=${this.isReadOnly}
                    .maxLength=${50}
                    .name=${'fax'}
                    .initial=${this.userInfo.fax}
                    .label=${'Fax'}
                >
                </tm-input>
            </div>
            <tm-wc-checkbox
                class="whitespace-nowrap"
                .label=${'Super user access'}
                .name=${'isSuperUser'}
                .initial=${this.userInfo.isSuperUser}
                .helperText=${'Super users have full access to all accounts and product features set up currently and in the future for this company.'}
                ?disabled=${this.isReadOnly}
            >
            </tm-wc-checkbox>
            <tm-wc-checkbox
                class="whitespace-nowrap"
                .label=${'Admin access'}
                .name=${'isAdmin'}
                .initial=${this.userInfo.isAdmin}
                .helperText=${'Admins are granted access to designated accounts and product feature abilities and have the ability to ad and edit other users.'}
                ?disabled=${this.isReadOnly}
            >
            </tm-wc-checkbox>
        </div>`;
    }

    private renderFooter() {
        if (this.isReadOnly) {
            return nothing;
        }
        return html`<div class="flex justify-end gap-[.825rem]">
            <tm-jh-button
                .name=${'save-and-continue'}
                appearance="primary"
                type=${TmButtonType.Submit}
                label="Save and Continue"
                class="p-0"
                .disabled=${!this.isValid}
                .loading=${this.isSaving}
            ></tm-jh-button>
        </div>`;
    }

    private renderForm() {
        return html`<tm-form
            @form-changed=${(e: TmFormChanged<FormModel>) => this.onFormChanged(e)}
            @form-submitted=${this.handleSaveCompanyUser}
        >
            <div class="mb-8">${this.renderAboutSection()}</div>
            <div>${this.renderFooter()}</div>
        </tm-form>`;
    }

    private onFormChanged(e: TmFormChanged<FormModel>) {
        const { isValid } = e.detail;
        this.isValid = isValid;
    }

    private async handleSaveCompanyUser(e: CustomEvent<{ model: FormModel; isPristine: boolean }>) {
        this.isSaving = true;
        this.dispatchEvent(new CustomEvent('begin-save', { detail: this.userInfo }));
        const { model } = e.detail;

        if (this.isBoApp && !this.companyId) {
            this.toastService.show(
                'Company ID is required to save the user.',
                TmToastPosture.Danger,
                true
            );
            return;
        }

        this.userInfo.name = model.name;
        this.userInfo.email = model.email;
        this.userInfo.loginId = model.loginId;
        this.userInfo.phone = model.phone;
        this.userInfo.fax = model.fax;
        this.userInfo.department = model.department;

        try {
            const result = await this.companyUserService.saveCompanyUserInformation(
                this.userId ?? 0,
                this.userInfo,
                this.companyId
            );

            if (!result.success) {
                throw new Error('Failed to save user information');
            }

            this.toastService.show('User Information saved successfully', TmToastPosture.Success);
            this.dispatchEvent(new CustomEvent('save', { detail: result }));
        } catch (e) {
            type Error = { message: string };
            const errorMessage = (e as Error).message ?? 'An error occurred while saving the user.';
            this.toastService.show(errorMessage, TmToastPosture.Danger, true);
        } finally {
            this.isSaving = false;
        }
    }

    public static get styles() {
        return css`
            :host {
                display: block;
            }
            jh-button {
                padding: 5px;
            }
        `;
    }
}
